import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import socketService from '../services/socketService';
import axios from 'axios';

interface ChatNotification {
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
}

export const useChatNotifications = () => {
  const { user, token } = useAuth();
  const [notification, setNotification] = useState<ChatNotification>({
    unreadCount: 0,
    isLoading: true,
    error: null
  });

  const fetchUnreadCount = async () => {
    try {
      setNotification(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Vérifier que l'utilisateur est authentifié et que le token est configuré
      if (!user || !token) {
        console.log('User not authenticated, skipping unread count fetch');
        setNotification({
          unreadCount: 0,
          isLoading: false,
          error: null
        });
        return;
      }
      
      // Vérifier que le token est bien configuré dans axios
      if (!axios.defaults.headers.common['Authorization']) {
        console.log('Token not configured in axios, waiting...');
        // Attendre un peu que le token soit configuré
        setTimeout(() => {
          if (axios.defaults.headers.common['Authorization']) {
            fetchUnreadCount();
          }
        }, 100);
        return;
      }
      
    // console.log('Fetching unread count for user:', user.id);
    // console.log('Authorization header:', axios.defaults.headers.common['Authorization']);
      const response = await axios.get('/api/chat/unread-count');
      setNotification({
        unreadCount: response.data.unreadCount || 0,
        isLoading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching chat notifications:', error);
      setNotification({
        unreadCount: 0,
        isLoading: false,
        error: 'Erreur lors du chargement des notifications'
      });
    }
  };

  const markAsRead = (decrementBy?: number) => {
    if (decrementBy !== undefined) {
      setNotification(prev => ({ 
        ...prev, 
        unreadCount: Math.max(0, prev.unreadCount - decrementBy) 
      }));
    } else {
      setNotification(prev => ({ ...prev, unreadCount: 0 }));
    }
  };

  const incrementUnreadCount = () => {
    setNotification(prev => ({ ...prev, unreadCount: prev.unreadCount + 1 }));
  };

  useEffect(() => {
    if (user && token) {
      // console.log('User authenticated, fetching unread count');
      // Ajouter un petit délai pour s'assurer que le token est configuré dans axios
      const timeoutId = setTimeout(() => {
        fetchUnreadCount();
      }, 200);
      
      // Rafraîchir toutes les 30 secondes
      const interval = setInterval(fetchUnreadCount, 30000);
      
      return () => {
        clearTimeout(timeoutId);
        clearInterval(interval);
      };
    } else {
      console.log('User not authenticated, clearing notifications');
      setNotification({
        unreadCount: 0,
        isLoading: false,
        error: null
      });
    }
  }, [user, token]);

  return {
    unreadCount: notification.unreadCount,
    isLoading: notification.isLoading,
    error: notification.error,
    refreshNotifications: fetchUnreadCount,
    markAsRead,
    incrementUnreadCount
  };
};
